import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import StatsBar from './components/StatsBar';
import Controls from './components/Controls';
import FireMap from './components/FireMap';
import EventList from './components/EventList';
import StatusBar from './components/StatusBar';
import { fetchWildfires } from './api/eonet';
import './App.css';

export default function App() {
  const [events, setEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [mapMode, setMapMode] = useState('heatmap');
  const [lastFetch, setLastFetch] = useState(null);
  const [flyTo, setFlyTo] = useState(null);

  const [filters, setFilters] = useState({
    days: 30,
    status: 'all',
    search: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWildfires(filters.days, filters.status);
      setEvents(data);
      setLastFetch(new Date());
    } catch (e) {
      setError('Failed to fetch from NASA EONET. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters.days, filters.status]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const q = filters.search.toLowerCase();
    const result = events.filter(e => {
      const nameMatch = !q || e.title.toLowerCase().includes(q);
      const statusMatch =
        filters.status === 'all' ||
        (filters.status === 'open' ? !e.closed : !!e.closed);
      return nameMatch && statusMatch;
    });
    setFiltered(result);
  }, [events, filters.search, filters.status]);

  const handleSelectEvent = useCallback((id) => {
    setSelectedId(id);
    const event = events.find(e => e.id === id);
    if (event && event.geometry?.length > 0) {
      const geo = event.geometry[event.geometry.length - 1];
      setFlyTo([geo.coordinates[1], geo.coordinates[0]]);
    }
  }, [events]);

  const stats = {
    total: events.length,
    open: events.filter(e => !e.closed).length,
    closed: events.filter(e => !!e.closed).length,
    regions: Math.min(new Set(events.map(e => Math.round(
      (e.geometry?.[0]?.coordinates?.[1] ?? 0) / 30
    ))).size * 3, 45),
  };

  return (
    <div className="app">
      <Header />
      <StatsBar stats={stats} lastFetch={lastFetch} />
      <Controls
        filters={filters}
        setFilters={setFilters}
        mapMode={mapMode}
        setMapMode={setMapMode}
        onRefresh={loadData}
        loading={loading}
      />
      <FireMap
        events={filtered}
        selectedId={selectedId}
        mapMode={mapMode}
        onSelectEvent={handleSelectEvent}
        flyTo={flyTo}
      />
      <StatusBar loading={loading} error={error} count={filtered.length} total={events.length} days={filters.days} />
      <EventList
        events={filtered}
        selectedId={selectedId}
        onSelectEvent={handleSelectEvent}
        loading={loading}
      />
    </div>
  );
}
